import { LocalVideoTrack, RemoteVideoTrack, TwilioError } from 'twilio-video';
export enum Pages {
  GeneralError = 'GENERAL_ERROR',
  Initial = 'INITIAL_PAGE_LOAD',
  InputAccessCode = 'INPUT_ACCESS_CODE',
  Invalid = 'INVALID',
  Loading = 'LOADING',
  Messenger = 'MESSENGER',
  RequestAccessCode = 'REQUEST_ACCESS_CODE',
  TooManyRequests = 'TOO_MANY_REQUESTS',
}

export enum MessageType {
  EscalationExecutionChange = 'ESCALATION_EXECUTION_CHANGE',
  GroupMemberShipChange = 'GROUP_MEMBERSHIP_CHANGE',
  HigherContinuation = 'HIGHER_CONTINUATION',
  LowerContinuation = 'LOWER_CONTINUATION',
  UserSent = 'USER_SENT',
}

export enum SenderStatus {
  Sending = 'SENDING',
  Sent = 'SENT',
  Failed = 'FAILED',
  Retrying = 'RETRYING',
  New = 'NEW',
  NA = 'NA',
}

export interface User {
  avatarUrl?: string;
  displayName?: string;
  firstName?: string;
  id: string;
  lastName?: string;
}

export interface Attachment {
  contentType: string;
  id: string;
  name: string;
  size: number;
}

export interface Message {
  $lastModified: unknown;
  attachments: Attachment[];
  body: string;
  createdAt: Date;
  id: string;
  isOutgoing: boolean;
  messageType: MessageType;
  metadata?: { payload?: { title: string } }[];
  sender: User;
  senderStatus: SenderStatus;
  serverId: string;
  subType: string;
}
export interface InputChangeEvent {
  persist: () => void;
  target: HTMLInputElement | HTMLTextAreaElement;
}

export interface Conversation {
  id: string;
  lastMessage: Message;
  messages: Message[];
  timeline: Message[];
}

export interface Organization {
  id: string;
  name: string;
  conversations: Conversation[];
}

export interface CallEventPayload {
  callId: string;
  callerId: string;
  date: string;
  isVideo: boolean;
  name: string;
  organizationId: string;
  recipientId: string;
  userToken?: string;
}

export interface CallEvent {
  accessToken: string;
  accessUrl: string;
  callStatus: string;
  conversationId: string;
  payload: CallEventPayload;
  roomName: string;
  ttl: number;
  type: string;
}

export type CallType = 'vwr' | 'pfma';

export type VisitStatusUpdateEvent = {
  organizationId: string;
  previousStatus: string;
  roomId: string;
  status: string;
  version: number;
  visitId: string;
};

export type VWRCallStatus = 'invite' | 'complete' | 'expire';

export type VWRCallEvent = {
  accessToken: string;
  connected_participants: unknown[];
  is_video: boolean;
  participants_tokens: string[];
  provider_display_name: string;
  receivedAt: Date;
  room_name: string;
  status: VWRCallStatus;
  ttl: number;
  twilio_room_name: string;
  visit_id: string;
  visitor_account_token: string;
  visitor_display_name: string;
  vwr_visitor_access_token: string;
  vwr_visitor_access_url: string;
};

declare module 'twilio-video' {
  interface LocalParticipant {
    setBandwidthProfile: (bandwidthProfile: BandwidthProfileOptions) => void;
    publishTrack(
      track: LocalTrack,
      options?: { priority: Track.Priority }
    ): Promise<LocalTrackPublication>;
  }

  interface VideoCodecSettings {
    simulcast?: boolean;
  }

  interface LocalVideoTrack {
    isSwitchedOff: undefined;
    setPriority: undefined;
  }

  interface RemoteVideoTrack {
    isSwitchedOff: boolean;
    setPriority: (priority: Track.Priority | null) => void;
  }

  interface VideoBandwidthProfileOptions {
    trackSwitchOffMode?: 'predicted' | 'detected' | 'disabled';
  }
}

declare global {
  interface MediaDevices {
    getDisplayMedia(constraints: MediaStreamConstraints): Promise<MediaStream>;
  }
}

export type Callback = (...args: unknown[]) => void;

export type ErrorCallback = (error: TwilioError) => void;

export type IVideoTrack = LocalVideoTrack | RemoteVideoTrack;

export type ExtendedFile = File & { unacceptedType: boolean };
